// Version 1 reader mirrors the backend contract. Never render stored HTML.
export const readRichText = (value) => {
  if (value === null || value === undefined || value === '')
    return { document: null };
  try {
    if (typeof value !== 'string' || new Blob([value]).size > 256 * 1024)
      throw Error();
    const doc = JSON.parse(value);
    const keys = (obj, allowed) => {
      if (
        !obj ||
        typeof obj !== 'object' ||
        Array.isArray(obj) ||
        Object.keys(obj).some((key) => !allowed.includes(key))
      )
        throw Error();
    };
    keys(doc, ['version', 'type', 'content']);
    if (doc.version !== 1 || doc.type !== 'doc' || !Array.isArray(doc.content))
      throw Error();
    let nodes = 0,
      characters = 0,
      populated = false;
    const visit = (node, depth, context) => {
      if (++nodes > 2000 || depth > 12) throw Error();
      keys(node, ['type', 'attrs', 'content', 'text', 'marks']);
      if (context === 'inline') {
        if (node.type === 'hardBreak') {
          keys(node, ['type']);
          return;
        }
        keys(node, ['type', 'text', 'marks']);
        if (
          node.type !== 'text' ||
          typeof node.text !== 'string' ||
          !node.text.length
        )
          throw Error();
        characters += Array.from(node.text).length;
        if (characters > 20000) throw Error();
        populated ||= !!node.text.trim();
        if (node.marks !== undefined) {
          if (!Array.isArray(node.marks) || node.marks.length > 4)
            throw Error();
          const used = new Set();
          for (const mark of node.marks) {
            keys(mark, ['type', 'attrs']);
            if (
              !['bold', 'italic', 'underline', 'link'].includes(mark.type) ||
              used.has(mark.type)
            )
              throw Error();
            used.add(mark.type);
            if (mark.type === 'link') {
              keys(mark.attrs, ['href']);
              const href = mark.attrs.href;
              if (
                typeof href !== 'string' ||
                href.length > 2048 ||
                /\s/u.test(href) ||
                Array.from(href).some(
                  (char) =>
                    char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127
                ) ||
                !/^https?:\/\//i.test(href)
              )
                throw Error();
              const url = new URL(href);
              if (!url.hostname || url.username || url.password) throw Error();
            } else keys(mark, ['type']);
          }
        }
        return;
      }
      if (
        context === 'item'
          ? node.type !== 'listItem'
          : !['paragraph', 'heading', 'bulletList', 'orderedList'].includes(
              node.type
            )
      )
        throw Error();
      keys(
        node,
        node.type === 'heading'
          ? ['type', 'attrs', 'content']
          : ['type', 'content']
      );
      if (node.type === 'heading') {
        keys(node.attrs, ['level']);
        if (![1, 2].includes(node.attrs.level)) throw Error();
      }
      const content = node.content === undefined ? [] : node.content;
      if (!Array.isArray(content)) throw Error();
      if (
        ['listItem', 'bulletList', 'orderedList'].includes(node.type) &&
        !content.length
      )
        throw Error();
      if (node.type === 'listItem' && content[0]?.type !== 'paragraph')
        throw Error();
      const next = ['paragraph', 'heading'].includes(node.type)
        ? 'inline'
        : node.type === 'listItem'
          ? 'block'
          : 'item';
      content.forEach((child) => visit(child, depth + 1, next));
    };
    doc.content.forEach((node) => visit(node, 1, 'block'));
    return { document: populated ? doc : null, characters, nodes };
  } catch {
    return { error: 'Rich text cannot be displayed.' };
  }
};

// Bound previews by both text length and structure, preserving formatting and order.
export const richTextExcerpt = (doc) => {
  let remaining = 1000,
    nodes = 40;
  const copy = (node) => {
    if (remaining <= 0 || nodes-- <= 0) return null;
    if (node.type === 'text') {
      const chars = Array.from(node.text);
      const text = chars.slice(0, remaining).join('');
      remaining -= chars.length;
      return { ...node, text };
    }
    if (node.type === 'hardBreak') return { ...node };
    const content = (node.content || []).map(copy).filter(Boolean);
    return content.length ? { ...node, content } : null;
  };
  return { ...doc, content: doc.content.map(copy).filter(Boolean) };
};
